/**
 * Utilities for client-side manuscript chunking and merging of AI extraction
 * results. Pure functions; no I/O.
 */

export const DEFAULT_CHUNK_SIZE = 20000; // characters (~4k words)

/**
 * Split text into chunks of roughly maxChars, breaking on paragraph
 * boundaries so sentences are never split across chunks.
 */
export const chunkText = (text, maxChars = DEFAULT_CHUNK_SIZE) => {
  if (!text || typeof text !== 'string') return [];
  const trimmed = text.trim();
  if (trimmed.length === 0) return [];
  if (trimmed.length <= maxChars) return [trimmed];

  const paragraphs = trimmed.split(/\n{2,}|\r\n{2,}/);
  const chunks = [];
  let current = '';

  paragraphs.forEach((paragraph) => {
    const cleaned = paragraph.trim();
    if (!cleaned) return;

    // A single paragraph larger than maxChars gets hard-split on sentence ends
    if (cleaned.length > maxChars) {
      if (current) {
        chunks.push(current);
        current = '';
      }
      const sentences = cleaned.split(/(?<=[.!?])\s+/);
      sentences.forEach((sentence) => {
        if (current.length + sentence.length + 1 > maxChars && current) {
          chunks.push(current);
          current = '';
        }
        current = current ? `${current} ${sentence}` : sentence;
      });
      return;
    }

    if (current.length + cleaned.length + 2 > maxChars && current) {
      chunks.push(current);
      current = '';
    }
    current = current ? `${current}\n\n${cleaned}` : cleaned;
  });

  if (current) {
    chunks.push(current);
  }

  return chunks;
};

const normalizeName = (name) =>
  String(name || '')
    .trim()
    .toLowerCase();

/**
 * Merge per-chunk extraction results into a single payload.
 * - characters deduped by fullName (mentions summed, longest description kept)
 * - timeline events deduped by title + date
 * - relationships deduped by from + to + type
 */
export const mergeExtractions = (results) => {
  const characterMap = new Map();
  const eventMap = new Map();
  const relationshipMap = new Map();

  (results || []).forEach((result) => {
    (result?.characters || []).forEach((character) => {
      const fullName = character.fullName || character.full_name;
      const key = normalizeName(fullName);
      if (!key) return;
      const existing = characterMap.get(key);
      if (!existing) {
        characterMap.set(key, { ...character, fullName });
      } else {
        characterMap.set(key, {
          ...existing,
          mentions: (existing.mentions || 0) + (character.mentions || 0),
          description:
            (character.description || '').length > (existing.description || '').length
              ? character.description
              : existing.description,
          traits: Array.from(new Set([...(existing.traits || []), ...(character.traits || [])])),
          role: existing.role === 'protagonist' ? existing.role : character.role || existing.role,
        });
      }
    });

    (result?.timelineEvents || []).forEach((event) => {
      const key = `${normalizeName(event.title)}|${normalizeName(event.date)}`;
      if (!eventMap.has(key)) {
        eventMap.set(key, event);
      }
    });

    (result?.relationships || []).forEach((relationship) => {
      const key = `${normalizeName(relationship.from)}|${normalizeName(relationship.to)}|${relationship.type}`;
      const mirrorKey = `${normalizeName(relationship.to)}|${normalizeName(relationship.from)}|${relationship.type}`;
      const symmetric = relationship.type === 'sibling' || relationship.type === 'spouse';
      if (!relationshipMap.has(key) && !(symmetric && relationshipMap.has(mirrorKey))) {
        relationshipMap.set(key, relationship);
      }
    });
  });

  return {
    characters: Array.from(characterMap.values()),
    timelineEvents: Array.from(eventMap.values()),
    relationships: Array.from(relationshipMap.values()),
  };
};
